/**
 * Instant Quote pricing — all numbers live here so a price change is a
 * one-file edit.
 *
 * Prices below are the FINAL prices we advertise for a home up to 2,500 sq ft
 * (the "$100 off" coupon is presented as an already-applied discount, so the
 * struck-through "was" price is simply final + $100).
 */

export type InstantServiceId =
  | "house"
  | "roof"
  | "window"
  | "gutter"
  | "concrete"
  | "pavers";

export type HomeSizeId = "under-2000" | "2000-2500" | "2500-3500" | "over-3500";

export const HOME_SIZES: {
  id: HomeSizeId;
  label: string;
  sub: string;
  multiplier: number;
}[] = [
  { id: "under-2000", label: "Under 2,000 sq ft", sub: "Smaller ranch or townhome", multiplier: 1.0 },
  { id: "2000-2500", label: "2,000 – 2,500 sq ft", sub: "Typical Capital Region colonial", multiplier: 1.0 },
  { id: "2500-3500", label: "2,500 – 3,500 sq ft", sub: "Larger two-story home", multiplier: 1.2 },
  { id: "over-3500", label: "3,500+ sq ft", sub: "Estate or multi-wing home", multiplier: 1.45 },
];

export const INSTANT_SERVICES: {
  id: InstantServiceId;
  label: string;
  sub: string;
  /** Final advertised price for a home up to 2,500 sq ft. */
  base: number;
  /** Whether the size multiplier applies. */
  scalesWithSize: boolean;
  /** "From" pricing — shown as a starting price rather than a fixed one. */
  fromOnly?: boolean;
}[] = [
  { id: "house", label: "House Wash", sub: "Soft-wash siding, trim & soffits", base: 299, scalesWithSize: true },
  { id: "roof", label: "Roof Soft Wash", sub: "Kills black streaks & moss", base: 599, scalesWithSize: true },
  { id: "window", label: "Window Cleaning", sub: "Exterior glass, streak-free", base: 249, scalesWithSize: true, fromOnly: true },
  { id: "gutter", label: "Gutter Cleaning", sub: "Debris out, downspouts flushed", base: 199, scalesWithSize: true },
  { id: "concrete", label: "Concrete Cleaning", sub: "Driveways & walkways", base: 200, scalesWithSize: false },
  { id: "pavers", label: "Paver Clean & Seal", sub: "Quoted by square footage", base: 499, scalesWithSize: false, fromOnly: true },
];

/**
 * Advertised combo prices (final, up to 2,500 sq ft). These override the
 * line-item math so the bundles we promote always land on exactly these
 * numbers. They still scale with home size.
 */
const LOCKED_COMBOS: { services: InstantServiceId[]; base: number }[] = [
  // `base` scales with home size; the gutter portion is added afterwards from
  // GUTTER_WITH_HOUSE_ADDON so gutters step up on their own ladder.
  { services: ["house", "window", "gutter"], base: 500 },
  { services: ["house", "window"], base: 499 },
  { services: ["house", "gutter"], base: 300 },
];

/**
 * Gutter cleaning added on to a house wash is a flat add-on that steps up with
 * home size (it is never run through the generic multiplier).
 */
const GUTTER_WITH_HOUSE_ADDON: Record<HomeSizeId, number> = {
  "under-2000": 99,
  "2000-2500": 99,
  "2500-3500": 149,
  "over-3500": 199,
};

/** Gutter cleaning sold on its own steps up on its own ladder. */
const GUTTER_STANDALONE: Record<HomeSizeId, number> = {
  "under-2000": 199,
  "2000-2500": 199,
  "2500-3500": 249,
  "over-3500": 299,
};

/**
 * No generic multi-service discount: the advertised bundles are already
 * discounted through LOCKED_COMBOS, and a floating percentage made every other
 * line's price move whenever an unrelated service was toggled.
 */
const BUNDLE_DISCOUNT = 0;


/** Minimum price — only applies when a house wash is part of the quote. */
const MIN_PRICE = 299;

/** Coupon: $100 off — only when the quote includes a house or roof wash. */
export const COUPON_AMOUNT = 100;
export const COUPON_CODE = "CAPPRO100";
const COUPON_SERVICES: InstantServiceId[] = ["house", "roof"];
/** Above this multiplier the "up to 2,500 sq ft" framing is dropped. */
const COUPON_SMALL_HOME_MAX_MULTIPLIER = 1.0;

/** The displayed range is always price → price + this amount. */
const RANGE_SPREAD = 100;

function roundTo10(n: number): number {
  return Math.round(n / 10) * 10;
}

/** Scale an advertised price, keeping exact numbers at the baseline size. */
function scale(price: number, mult: number): number {
  return mult === 1 ? price : roundTo10(price * mult);
}

export type QuoteBreakdown = {
  /** Line items after locked-combo merging, with per-line price. */
  lines: { id: string; label: string; price: number; fromOnly: boolean; lockedCombo: boolean }[];
  subtotal: number;
  bundleDiscountAmount: number;
  couponEligible: boolean;
  couponSmallHome: boolean;
  finalPrice: number;
  rangeLow: number;
  rangeHigh: number;
  /** True when any line is "from" pricing (pavers, windows). */
  hasFromPricing: boolean;
};

export function calculateInstantQuote(
  selected: InstantServiceId[],
  sizeId: HomeSizeId,
): QuoteBreakdown | null {
  const size = HOME_SIZES.find((s) => s.id === sizeId);
  if (!size || selected.length === 0) return null;
  const mult = size.multiplier;

  // Pick the largest locked combo fully covered by the selection.
  const combo = LOCKED_COMBOS.filter((c) => c.services.every((s) => selected.includes(s))).sort(
    (a, b) => b.services.length - a.services.length,
  )[0];

  const lines: QuoteBreakdown["lines"] = [];
  const covered = new Set<InstantServiceId>();

  if (combo) {
    lines.push({
      id: combo.services.join("+"),
      label: combo.services
        .map((id) => INSTANT_SERVICES.find((s) => s.id === id)!.label)
        .join(" + "),
      price:
        scale(combo.base, mult) +
        (combo.services.includes("gutter") ? GUTTER_WITH_HOUSE_ADDON[sizeId] : 0),
      fromOnly: false,
      lockedCombo: true,
    });
    combo.services.forEach((s) => covered.add(s));
  }

  const houseSelected = selected.includes("house");

  for (const svc of INSTANT_SERVICES) {
    if (!selected.includes(svc.id) || covered.has(svc.id)) continue;
    // Gutters bolted on to a house wash are a flat add-on, never full price.
    const isGutterAddOn = svc.id === "gutter" && houseSelected;
    const price = isGutterAddOn
      ? GUTTER_WITH_HOUSE_ADDON[sizeId]
      : svc.id === "gutter"
        ? GUTTER_STANDALONE[sizeId]
        : svc.scalesWithSize
          ? scale(svc.base, mult)
          : svc.base;
    lines.push({
      id: svc.id,
      label: svc.label,
      price,
      fromOnly: !!svc.fromOnly,
      lockedCombo: false,
    });
  }

  let subtotal = 0;
  let bundleDiscountAmount = 0;
  for (const line of lines) {
    subtotal += line.price;
    const isGutterAddon = line.id === "gutter" && houseSelected;
    if (BUNDLE_DISCOUNT > 0 && !line.lockedCombo && !isGutterAddon && selected.length >= 2) {
      const discount = roundTo10(line.price * BUNDLE_DISCOUNT);
      bundleDiscountAmount += discount;
      line.price -= discount;
    }
  }


  const afterBundle = subtotal - bundleDiscountAmount;
  const couponEligible = COUPON_SERVICES.some((s) => selected.includes(s));
  const couponSmallHome = couponEligible && mult <= COUPON_SMALL_HOME_MAX_MULTIPLIER;
  const finalPrice = houseSelected ? Math.max(afterBundle, MIN_PRICE) : afterBundle;

  return {
    lines,
    subtotal,
    bundleDiscountAmount,
    couponEligible,
    couponSmallHome,
    finalPrice,
    rangeLow: finalPrice,
    rangeHigh: finalPrice + RANGE_SPREAD,
    hasFromPricing: lines.some((l) => l.fromOnly),
  };
}

export const money = (n: number): string => `$${n.toLocaleString("en-US")}`;

/**
 * The amount a single selected service contributes to the current total.
 * Computed as a sequential prefix delta in INSTANT_SERVICES order, so the
 * shares of all selected services always add up to exactly the quoted total —
 * no more "lines say $300 but the price says $399".
 */
export function serviceShare(
  selected: InstantServiceId[],
  sizeId: HomeSizeId,
  id: InstantServiceId,
): number {
  if (!selected.includes(id)) return 0;
  const ordered = INSTANT_SERVICES.map((s) => s.id).filter((s) => selected.includes(s));
  const idx = ordered.indexOf(id);
  const withIt = calculateInstantQuote(ordered.slice(0, idx + 1), sizeId)?.finalPrice ?? 0;
  const without = idx === 0 ? 0 : (calculateInstantQuote(ordered.slice(0, idx), sizeId)?.finalPrice ?? 0);
  return Math.max(withIt - without, 0);
}
